import {useMemo} from 'react';
import {useProductIdentifier} from '../../../shared-deprecated/useProductIdentifier';
import {useOutfitIdentifier} from '../../../shared-deprecated/useOutfitIdentifier';
import {useStoryIdentifier} from '../../../shared-deprecated/useStoryIdentifier';
import {useCurrentPageType, useParams, useQueryParams} from '@aboutyou/router';
import {getEntityIdFromRouteParam} from '@aboutyou/utils';
import {convertGenderToTargetGroup} from '../../../shared-deprecated/useSelectedTargetGroup';

export const useEntityIdentifier = () => {
  const productIdentifier = useProductIdentifier();
  const brandIdentifier = useBrandIdentifier();
  const brandDiscoverIdentifier = useBrandDiscoverIdentifier();
  const outfitIdentifier = useOutfitIdentifier();
  const storyIdentifier = useStoryIdentifier();
  const categoryIdentifier = useCategoryIdentifier();

  const currentPageTye = useCurrentPageType();
  const genderParamIdentifier = useGenderParamIdentifier();

  return useMemo(() => {
    if (currentPageTye === 'home' || currentPageTye === 'welcome') {
      return {
        $case: 'browse' as const,
        browse: {
          targetGroup: genderParamIdentifier,
        },
      };
    }

    if (currentPageTye === 'searchResults') {
      return {
        $case: 'search' as const,
        search: {
          targetGroup: genderParamIdentifier,
        },
      };
    }

    if (currentPageTye === 'seoStream') {
      return {
        $case: 'seo' as const,
        seo: {
          targetGroup: genderParamIdentifier,
        },
      };
    }

    if (categoryIdentifier) {
      return {
        $case: 'category' as const,
        category: {
          identifier: categoryIdentifier,
        },
      };
    }

    if (productIdentifier) {
      return {
        $case: 'product' as const,
        product: {
          identifier: productIdentifier,
        },
      };
    }

    if (currentPageTye === 'brandShop' && brandIdentifier) {
      return {
        $case: 'brand' as const,
        brand: brandIdentifier,
      };
    }

    if (currentPageTye === 'brandDiscover' && brandDiscoverIdentifier) {
      return {
        $case: 'brandDiscover' as const,
        brandDiscover: brandDiscoverIdentifier,
      };
    }

    if (storyIdentifier) {
      return {
        $case: 'story' as const,
        story: {
          identifier: storyIdentifier,
        },
      };
    }

    if (outfitIdentifier) {
      return {
        $case: 'outfit' as const,
        outfit: {
          identifier: outfitIdentifier,
        },
      };
    }
  }, [
    brandIdentifier,
    categoryIdentifier,
    currentPageTye,
    genderParamIdentifier,
    outfitIdentifier,
    productIdentifier,
    storyIdentifier,
    brandDiscoverIdentifier,
  ]);
};

const useGenderParamIdentifier = () => {
  const queryParams = useQueryParams();
  const paramGender = queryParams.get('gender');

  if (paramGender) {
    return convertGenderToTargetGroup({
      gender: paramGender,
      defaultToFemale: false,
    });
  }
};

const useCategoryIdentifier = () => {
  const {categoryId} = useParams<{categoryId?: string}>();
  const id = getEntityIdFromRouteParam(categoryId);

  if (id) {
    return {
      $case: 'id' as const,
      id,
    };
  }
};

const useBrandIdentifier = () => {
  const {brandName} = useParams<{
    brandName?: string;
  }>();
  const queryParams = useQueryParams();
  const isBrandHub = queryParams.get('cluster') === 'true';
  const categoryParam = queryParams.get('category');
  const categoryId = categoryParam ? Number(categoryParam) : undefined;

  return brandName
    ? {
        brand: {
          identifier: {
            $case: 'slug' as const,
            slug: brandName,
          },
        },
        category: categoryId
          ? {
              id: categoryId,
            }
          : undefined,
        isBrandHub,
      }
    : null;
};

const useBrandDiscoverIdentifier = () => {
  const {brandId} = useParams<{brandId?: string}>();
  const genderParamIdentifier = useGenderParamIdentifier();
  const id = Number(brandId);

  if (!isNaN(id)) {
    return {
      targetGroup: genderParamIdentifier,
      brand: {
        identifier: {
          $case: 'id' as const,
          id,
        },
      },
    };
  }

  return null;
};
